import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import {
  GetPrivateRegionNodeHardwareBriefRes,
  GetPrivateRegionStatsOverviewRes,
} from '@/api/type-response'
import { ServerPrivateRegionInfo } from '@/api/type-server'
import { message } from '@/components/base/Notification'
import { PrivateRegionNodeType } from '@/consts/privateRegion'

export function useDashboardData(
  selectedRegion: number | null,
  domainInfos: ServerPrivateRegionInfo[],
  selectedModel?: string | number,
) {
  const { t } = useTranslation('privateRegion')
  const [regionInfo, setRegionInfo] = useState<ServerPrivateRegionInfo | null>(null)
  const [overviewData, setOverviewData] = useState<GetPrivateRegionStatsOverviewRes | null>(null)
  const [gpuBriefData, setGpuBriefData] = useState<GetPrivateRegionNodeHardwareBriefRes | null>(
    null,
  )
  const [loading, setLoading] = useState(false)

  // Set region info whenever selectedRegion or domainInfos changes
  useEffect(() => {
    const selectedRegionInfo = domainInfos.find((d) => d.region === selectedRegion) || null
    setRegionInfo(selectedRegionInfo)
  }, [selectedRegion, domainInfos])

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      if (selectedRegion === null) return

      setLoading(true)
      try {
        // Default params: type 0 (All node), this month (local timezone)
        const now = dayjs()
        const startTime = now.startOf('month')
        const endTime = now.endOf('month')

        const [overview, gpuBrief] = await Promise.all([
          API.getPrivateRegionOverview(
            {
              region: selectedRegion,
              type: PrivateRegionNodeType.AllNode,
              start_time: startTime.format(),
              end_time: endTime.format(),
              model: selectedModel ? String(selectedModel) : undefined,
            },
            { signal },
          ),
          API.getPrivateRegionNodeHardwareBrief(
            {
              region: selectedRegion,
              type: PrivateRegionNodeType.AllNode,
            },
            { signal },
          ),
        ])

        // Set default values if API fails, so UI can still render with empty/zero state
        setOverviewData(
          overview.code === 0
            ? overview
            : {
                nodesAll: 0,
                nodesOnline: 0,
                leasedTimeHour: 0,
                leasedTimeHourRateDelta: 0,
                occupancyRate: 0,
                occupancyRateDelta: 0,
              },
        )
        setGpuBriefData(
          gpuBrief.code === 0
            ? gpuBrief
            : {
                brief: {
                  quantityStats: { total: 0, online: 0, occupied: 0 },
                  modelStats: [],
                },
              },
        )
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') return
        message.error(t('errors.failedToLoadDashboardData'))
      } finally {
        setLoading(false)
      }
    },
    [selectedRegion, selectedModel, t],
  )

  useEffect(() => {
    if (selectedRegion === null) return

    const abortController = new AbortController()
    loadData(abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [selectedRegion, loadData])

  const refetch = useCallback(() => {
    if (selectedRegion !== null) {
      const abortController = new AbortController()
      loadData(abortController.signal)
    }
  }, [selectedRegion, loadData])

  return {
    regionInfo,
    overviewData,
    gpuBriefData,
    loading,
    refetch,
  }
}
